import { SerializedError } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { isString } from 'lodash';

export class AxiosErrorSerializer {
    constructor(protected error: AxiosError<unknown>) {
        this.getCode = this.getCode.bind(this);
        this.getName = this.getName.bind(this);
        this.getDebugData = this.getDebugData.bind(this);
        this.getMessage = this.getMessage.bind(this);
        this.getSerializedMessage = this.getSerializedMessage.bind(this);
        this.serialize = this.serialize.bind(this);
    }

    getCode() {
        return String(this.error.code);
    }

    getName() {
        const { status, statusText } = this.error.response || {};
        return `${status || this.error.name}${statusText ? ` - ${statusText}` : ''}`;
    }

    getDebugData() {
        const {
            data: responseData,
            config: { url, method, data: requestData },
        } = this.error.response || {
            data: {},
            status: '',
            statusText: '',
            config: {
                url: '',
                method: '',
                data: null,
            },
        };

        return {
            id: (responseData as { id: string }).id,
            method,
            url,
            data: requestData,
        };
    }

    getMessage() {
        const { data } = this.error.response || {};
        return `${(data as { error?: string; message?: string }).error ?? (data as { message?: string }).message ?? this.error.message}`;
    }

    getSerializedMessage(message: string, debugData?: ReturnType<AxiosErrorSerializer['getDebugData']>) {
        const { id, method, url, data } = debugData || {};
        const debugString = `
            ${id ? `Id: ${id}\n` : ''}
            ${url ? `Endpoint: ${method?.toUpperCase()} ${url}\n` : ''}
            ${isString(data) ? `Payload: \n${JSON.stringify(JSON.parse(data), null, 4)}` : ''}
        `.trim();

        return `${message}${!debugData ? '' : `\n\n${debugString}`}`.trim();
    }

    getFormattedMessage(message: string, debugData?: ReturnType<AxiosErrorSerializer['getDebugData']>) {
        const { id, method, url, data } = debugData || {};

        const formattedId = id ? (
            <>
                Id: {id}
                <br />
            </>
        ) : null;

        const formattedUrl = url ? (
            <>
                Endpoint: {`${method?.toUpperCase()} ${url}`}
                <br />
            </>
        ) : null;

        const formattedPayload = isString(data) ? (
            <>
                Payload: <br />
                {'    ' + JSON.stringify(JSON.parse(data), null, 4).replace(/\n/g, '\n    ')}`
            </>
        ) : null;

        return (
            <>
                {message}

                {(formattedId || formattedUrl || formattedPayload) && (
                    <pre>
                        <code>
                            {formattedId}
                            {formattedUrl}
                            {formattedPayload}
                        </code>
                    </pre>
                )}
            </>
        );
    }

    serialize(debug = false): SerializedError {
        return {
            name: this.getName(),
            code: this.getCode(),
            stack: this.error.stack,
            message: this.getSerializedMessage(this.getMessage(), debug ? this.getDebugData() : undefined),
            formattedMessage: this.getFormattedMessage(this.getMessage(), debug ? this.getDebugData() : undefined),
        };
    }
}

export class OrbisErrorSerializer extends AxiosErrorSerializer {
    override getCode() {
        return (this.error as { orbisErrorCode?: string }).orbisErrorCode || super.getCode();
    }

    override getMessage() {
        return (this.error as { orbisErrorMessage?: string }).orbisErrorMessage || super.getMessage();
    }
}
